import { db } from '~/config/firebase.config';
import {
    addDoc,
    collection,
    getDocs,
    limit,
    onSnapshot,
    orderBy,
    query,
    serverTimestamp,
    where,
    writeBatch,
} from 'firebase/firestore';
import { HighscoreCouponService } from '~/services/coupons/HighscoreCouponService';

const getHighscoresRef = (clientID: string, gameID: string) =>
    collection(db, 'clients', clientID, 'games', gameID, 'highscores');

/**
 * Get highscore related to a document
 * @param docHighscore the document
 * @returns
 */
const getHighscore = async (docHighscore: any) => {
    let user;
    try {
        user = docHighscore.data();
        // Include document ID for precise filtering
        user.id = docHighscore.id;
    } catch (err) {
        console.error(err);
    }
    return user;
};

/**
 * Convert snapshot into a list of highscores
 * @param querySnapshot
 * @returns
 */
const getHighscores = async (querySnapshot: any) => {
    const hsPromises: any[] = [];
    querySnapshot.forEach((document: any) => {
        hsPromises.push(getHighscore(document));
    });
    const allPromises = await Promise.all(hsPromises);
    return allPromises;
};

/**
 * Filters highscores to keep only the highest score per player (by uid)
 * @param scores Array of highscore records
 * @param limit Optional limit to return only top N unique players
 * @returns Array with only the highest score per player, sorted by score descending
 */
const getUniquePlayerHighscores = (scores: any[], limit?: number): any[] => {
    const playerMap = new Map<string, any>();

    // Keep only the highest score for each player
    scores.forEach((score) => {
        if (!score?.uid) return;
        const existing = playerMap.get(score.uid);
        if (!existing || score.score > existing.score) {
            playerMap.set(score.uid, score);
        }
    });

    // Convert back to array and sort by score descending
    const uniqueScores = Array.from(playerMap.values()).sort((a, b) => b.score - a.score);

    // Apply limit if specified
    return limit && limit > 0 ? uniqueScores.slice(0, limit) : uniqueScores;
};

async function saveScore(clientID: string, gameID: string, uid: string, score: number, data: any, screenID?: string) {
    const highscoresRef = getHighscoresRef(clientID, gameID);

    const newScore = {
        ...data,
        uid,
        date: serverTimestamp(),
        score,
    };
    try {
        // Save the score first to ensure all scores are in the database before checking coupons
        const docRef = await addDoc(highscoresRef, newScore);

        // Then check for coupon eligibility, excluding this player's just-created score by document ID
        await HighscoreCouponService.handleGameEnded({
            clientID,
            gameID,
            playerId: uid,
            score,
            excludeScoreId: docRef.id, // Exclude this specific score document from max score check
            screenID, // Restrict offers to this screen when set (auto mode)
        });
    } catch (err) {
        console.error('Cannot save highscore or trigger coupon.');
        console.error(err);
    }
}

async function retrieveHighscores(clientID: string, gameID: string, total: number, uniquePlayersOnly: boolean = true) {
    const fetchLimit = total <= 0 ? 10000 : uniquePlayersOnly ? Math.max(total * 3, 10) : total; // Fetch more records to account for duplicate players, then filter to unique players

    const scoresRef = HighscoresAPI.ref(clientID, gameID);
    const q = query(scoresRef, orderBy('score', 'desc'), orderBy('date'), limit(fetchLimit));
    let highscores;
    try {
        const querySnapshot = await getDocs(q);
        highscores = await getHighscores(querySnapshot);

        if (uniquePlayersOnly) {
            highscores = getUniquePlayerHighscores(highscores, total > 0 ? total : undefined);
        } else {
            highscores.sort((a: any, b: any) => b.score - a.score);
            if (total > 0) {
                highscores = highscores.slice(0, total);
            }
        }
    } catch (err) {
        console.error('Cannot get highscores.');
        console.error(err);
    }

    return highscores || [];
}

async function retrieveDailyHighscores(clientID: string, gameID: string, total: number) {
    const fetchLimit = total <= 0 ? 10000 : Math.max(total * 3, 10);

    const scoresRef = HighscoresAPI.ref(clientID, gameID);
    let highscores;
    const startOfDay = new Date();
    startOfDay.setHours(0, 0, 0, 0);
    const endOfDay = new Date(startOfDay.getTime() + 86400000);

    const q = query(
        scoresRef,
        where('date', '>=', startOfDay),
        where('date', '<', endOfDay),
        orderBy('date'),
        orderBy('score', 'desc'),
        limit(fetchLimit)
    );

    try {
        const querySnapshot = await getDocs(q);
        highscores = await getHighscores(querySnapshot);
        highscores = getUniquePlayerHighscores(highscores, total > 0 ? total : undefined);
        highscores.sort((a: any, b: any) => b.score - a.score);
    } catch (err) {
        console.error('Cannot get highscores.');
        console.error(err);
    }

    return highscores || [];
}

async function retrieveWindowHighscores(
    clientID: string,
    gameID: string,
    start?: Date,
    end?: Date,
    total: number = 1,
    uniquePlayersOnly: boolean = false
) {
    const limitValue = total <= 0 ? 10000 : total;
    const scoresRef = HighscoresAPI.ref(clientID, gameID);
    let qRef: any;
    if (start && end) {
        qRef = query(
            scoresRef,
            where('date', '>=', start),
            where('date', '<', end),
            orderBy('date'),
            orderBy('score', 'desc'),
            limit(limitValue)
        );
    } else {
        qRef = query(scoresRef, orderBy('score', 'desc'), orderBy('date'), limit(limitValue));
    }
    const snap = await getDocs(qRef);
    let list = await getHighscores(snap);

    list.sort((a: any, b: any) => b.score - a.score);

    if (uniquePlayersOnly) {
        list = getUniquePlayerHighscores(list, total > 0 ? total : undefined);
    } else if (total > 0) {
        list = list.slice(0, total);
    }

    return list;
}

function subscribe(clientID: string, gameID: string) {
    const ref = HighscoresAPI.ref(clientID, gameID);
    return (onValuehange: () => void) => onSnapshot(ref, onValuehange);
}

async function deleteAllScores(clientID: string, gameID: string): Promise<void> {
    const scoresRef = getHighscoresRef(clientID, gameID);

    try {
        const querySnapshot = await getDocs(scoresRef);

        if (querySnapshot.empty) return;

        // Firestore writeBatch supports max 500 operations
        const BATCH_SIZE = 500;
        const docs = querySnapshot.docs;

        for (let i = 0; i < docs.length; i += BATCH_SIZE) {
            const batch = writeBatch(db);
            const chunk = docs.slice(i, i + BATCH_SIZE);
            chunk.forEach((doc) => {
                batch.delete(doc.ref);
            });
            await batch.commit();
        }
    } catch (err) {
        console.error('Cannot delete highscores.');
        console.error(err);
        throw err;
    }
}

export const HighscoresAPI = {
    saveScore,
    ref: getHighscoresRef,
    getSnapshot: retrieveHighscores,
    getDailySnapshot: retrieveDailyHighscores,
    getWindowSnapshot: retrieveWindowHighscores,
    subscribe,
    deleteAllScores,
};
